import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useGiftCardTransactions } from '../hooks/use-gift-card-transactions';

/** The ledger of one card; amounts are signed, redemptions negative. */
export function GiftCardTransactionsCard({ storeId, cardId }: { storeId: string; cardId: string }) {
  const { t } = useTranslation('giftCards');
  const format = useFormat();
  const query = useGiftCardTransactions(storeId, cardId);
  const rows = query.data ?? [];

  return (
    <FormSection
      title={t('ledger.title')}
      description={t('ledger.description')}
      tour="gift-cards.detail.transactions"
    >
      {query.isPending ? (
        <Skeleton className="h-24" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('ledger.empty')}</p>
      ) : (
        <Table aria-label={t('ledger.title')}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ledger.kind')}</TableHead>
              <TableHead className="text-right">{t('ledger.amount')}</TableHead>
              <TableHead>{t('ledger.order')}</TableHead>
              <TableHead>{t('ledger.note')}</TableHead>
              <TableHead>{t('ledger.when')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{t(`ledger.kinds.${row.kind}`)}</TableCell>
                <TableCell
                  className={cn(
                    'text-right tabular-nums',
                    row.amount.amount < 0 ? 'text-destructive' : 'text-success',
                  )}
                >
                  {row.amount.amount > 0 ? '+' : ''}
                  {format.money(row.amount)}
                </TableCell>
                <TableCell>
                  {row.orderId ? (
                    <Link
                      to="/s/$storeId/orders/$orderId"
                      params={{ storeId, orderId: row.orderId }}
                      className="hover:underline"
                    >
                      {t('ledger.viewOrder')}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{row.note ?? ''}</TableCell>
                <TableCell className="text-muted-foreground" title={format.utc(row.createdAt)}>
                  {format.relative(row.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </FormSection>
  );
}
